import { tr } from '@/lib/i18n-core';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { useState, type KeyboardEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CalendarRange, ListChecks, Tag, Users } from 'lucide-react';
import type { PayerPlanDto } from '@his/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post, put, qs } from '@/lib/api';
import { useBranchLock } from '@/lib/branch';
import { describeError, useAuth } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Badge, Button, Checkbox, DatePicker, Field, FormSkeleton, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import type { ChargeItem, Tariff } from './ChargeMasterPage';

const PATIENT_CATEGORIES = ['general', 'panel', 'insurance', 'staff', 'charity', 'vip'] as const;

interface TariffPriceRow {
  id: string;
  chargeItem: { id: string; code: string; name: string; category: string; basePriceMinor: number };
  priceMinor: number;
  discountPct: number;
}

function toTariffForm(tariff: Tariff | null) {
  return {
    code: tariff?.code ?? '',
    name: tariff?.name ?? '',
    categories: (tariff?.categories ?? ['general']) as string[],
    payerId: tariff?.payer?.id ?? '',
    planId: tariff?.planId ?? '',
    forBranchId: tariff?.forBranchId ?? '',
    validFrom: tariff?.validFrom ? tariff.validFrom.slice(0, 10) : '',
    validTo: tariff?.validTo ? tariff.validTo.slice(0, 10) : '',
    priority: String(tariff?.priority ?? 100),
    isActive: true,
  };
}

function keepEnterInCell(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key === 'Enter') event.preventDefault();
}

export function TariffPage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/billing/charges?tab=tariffs');

  const tariffs = useQuery({
    queryKey: ['tariffs'],
    queryFn: () => get<Tariff[]>('/tariffs'),
    enabled: Boolean(id),
  });

  if (!id) return <TariffForm key="new" tariff={null} returnTo={returnTo} />;
  if (tariffs.isLoading) return <FormSkeleton />;
  const tariff = tariffs.data?.find((row) => row.id === id);
  if (!tariff) return <Alert variant="destructive">{tr('billing', 'that_record_could_not_be_opened')}</Alert>;
  return <TariffForm key={tariff.id} tariff={tariff} returnTo={returnTo} />;
}

function TariffForm({ tariff, returnTo }: { tariff: Tariff | null; returnTo: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasModule, user } = useAuth();
  const hasInsurance = hasModule('insurance');
  const branches = user?.branches ?? [];
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const lock = useBranchLock();
  const [form, setForm] = useState(() => ({ ...toTariffForm(tariff), ...(!tariff && lock.locked ? { forBranchId: lock.branchId } : {}) }));
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Record<string, { price: string; discount: string }>>({});

  const payers = useQuery({
    queryKey: ['payers', 'for-tariff'],
    queryFn: () => get<{ id: string; name: string }[]>('/insurance/payers'),
    enabled: hasInsurance,
    retry: false,
  });

  const plans = useQuery({
    queryKey: ['payer-plans', form.payerId],
    queryFn: () => get<PayerPlanDto[]>(`/insurance/payers/${form.payerId}/plans`),
    enabled: hasInsurance && Boolean(form.payerId),
    retry: false,
  });

  const charges = useQuery({
    queryKey: ['charge-items', 'for-tariff', search],
    queryFn: () => get<{ data: ChargeItem[] }>(`/charge-items${qs({ q: search || undefined, packages: 1, limit: 200 })}`),
    enabled: Boolean(tariff),
  });

  const tariffId = tariff?.id ?? '';
  const prices = useQuery({
    queryKey: ['tariff-prices', tariffId],
    queryFn: () => get<TariffPriceRow[]>(`/tariffs/${tariffId}/prices`),
    enabled: Boolean(tariffId),
  });

  const priced = new Map((prices.data ?? []).map((row) => [row.chargeItem.id, row]));

  const saveTariff = useMutation({
    mutationFn: () => {
      const body = {
        code: form.code,
        name: form.name,
        categories: form.categories,
        payerId: form.payerId || null,
        planId: form.payerId && form.planId ? form.planId : null,
        forBranchId: form.forBranchId || null,
        validFrom: form.validFrom || null,
        validTo: form.validTo || null,
        priority: Number(form.priority),
        isActive: form.isActive,
      };
      return tariff ? put<Tariff>(`/tariffs/${tariff.id}`, body) : post<Tariff>('/tariffs', body);
    },
    onSuccess: (saved) => {
      toast.success(tariff ? tr('billing', 'tariff_updated') : tr('billing', 'tariff_added'));
      void queryClient.invalidateQueries({ queryKey: ['tariffs'] });
      if (tariff) navigate(returnTo, { replace: true });
      else navigate(`/billing/charges/price-lists/${saved.id}?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const savePrices = useMutation({
    mutationFn: () => {
      const rows = Object.entries(draft)
        .filter(([, value]) => value.price.trim() !== '')
        .map(([chargeItemId, value]) => ({
          chargeItemId,
          priceMinor: minorFromInput(value.price),
          discountPct: Number(value.discount || 0),
        }));
      return put(`/tariffs/${tariffId}/prices`, { prices: rows });
    },
    onSuccess: () => {
      toast.success(tr('billing', 'rates_saved'));
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['tariff-prices', tariffId] });
      void queryClient.invalidateQueries({ queryKey: ['tariffs'] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const removePrice = useMutation({
    mutationFn: (chargeItemId: string) => del(`/tariffs/${tariffId}/prices/${chargeItemId}`),
    onSuccess: () => {
      toast.success(tr('billing', 'rate_removed'), tr('billing', 'that_service_falls_back_to_its_list_price'));
      void queryClient.invalidateQueries({ queryKey: ['tariff-prices', tariffId] });
      void queryClient.invalidateQueries({ queryKey: ['tariffs'] });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const pendingRates = Object.values(draft).filter((value) => value.price.trim() !== '').length;
  const automatic = tariff?.kind === 'nphies';
  const missing: string[] = [];
  if (form.code.trim().length < 2) missing.push('a code of at least two characters');
  if (form.name.trim().length < 2) missing.push('a name');
  if (form.categories.length === 0 && !form.payerId) missing.push('at least one patient category, or a payer');

  const dirty = !saveTariff.isSuccess && (JSON.stringify(form) !== JSON.stringify(toTariffForm(tariff)) || pendingRates > 0);

  return (
    <FormScreen
      wide
      title={tariff ? tr('billing', 'edit_tariff') : tr('billing', 'add_tariff')}
      description={tr('billing', 'a_tariff_prices_services_for_a_payer_or_a')}
      eyebrow={tr('billing', 'charge_master')}
      icon={ListChecks}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (automatic || missing.length > 0) return;
        setError(null);
        saveTariff.mutate();
      }}
    >
      {automatic && (
        <Alert>
          <span className="inline-flex items-center gap-2">
            <Badge variant="secondary">{tr('billing', 'auto')}</Badge>
            {tr('billing', 'maintained_from_the_services_tab')}
          </span>
        </Alert>
      )}

      <FormSection title={tr('billing', 'tariff')} icon={Tag}>
        <Field label={tr('billing', 'code')} required>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={Boolean(tariff)} />
        </Field>
        <Field label={tr('billing', 'name')} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={tr('billing', 'active')} className="sm:col-span-2">
          <Checkbox checked={form.isActive} onCheckedChange={(checked) => setForm({ ...form, isActive: checked })} label={tr('billing', 'price_with_this_tariff')} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'applies_to')} icon={Users}>
        <Field label={tr('billing', 'applies_to')} required hint={tr('billing', 'the_patient_categories_this_tariff_prices')} className="sm:col-span-2">
          <div className="flex flex-wrap gap-3">
            {PATIENT_CATEGORIES.map((category) => (
              <Checkbox
                key={category}
                checked={form.categories.includes(category)}
                onCheckedChange={(checked) =>
                  setForm({
                    ...form,
                    categories: checked ? [...form.categories, category] : form.categories.filter((c) => c !== category),
                  })
                }
                label={humanize(category, 'patient_category')}
              />
            ))}
          </div>
        </Field>
        {hasInsurance && (
          <Field
            label={tr('billing', 'payer')}
            hint={payers.isError ? tr('billing', 'needs_the_payer_master_permission') : tr('billing', 'leave_empty_for_a_category_tariff')}
          >
            <Select value={form.payerId} onChange={(e) => setForm({ ...form, payerId: e.target.value })} disabled={payers.isError}>
              <option value="">—</option>
              {payers.data?.map((payer) => (
                <option key={payer.id} value={payer.id}>
                  {payer.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {hasInsurance && form.payerId && (
          <Field label={tr('billing', 'plan')} hint={tr('billing', 'only_patients_on_this_plan_of_the_payer_get')}>
            <Select value={form.planId} onChange={(e) => setForm({ ...form, planId: e.target.value })} disabled={plans.isError}>
              <option value="">{tr('billing', 'every_plan_of_this_payer')}</option>
              {plans.data?.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {branches.length > 1 && (
          <Field label={tr('billing', 'branch')} hint={tr('billing', 'leave_empty_for_every_branch')}>
            <Select value={form.forBranchId} readOnly={lock.locked} onChange={(e) => setForm({ ...form, forBranchId: e.target.value })}>
              <option value="">{tr('billing', 'every_branch')}</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('billing', 'validity')} icon={CalendarRange} columns={3}>
        <Field label={tr('billing', 'valid_from')} hint={tr('billing', 'leave_empty_to_start_at_once')}>
          <DatePicker value={form.validFrom} onChange={(value) => setForm({ ...form, validFrom: value })} />
        </Field>
        <Field label={tr('billing', 'valid_until')} hint={tr('billing', 'leave_empty_for_no_end_after_it_the_next_price')}>
          <DatePicker value={form.validTo} onChange={(value) => setForm({ ...form, validTo: value })} />
        </Field>
        <Field label={tr('billing', 'priority')} hint={tr('billing', 'lower_wins_a_payer_contract_outranks_a_category')}>
          <Input type="number" min={0} max={1000} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="tabular" />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      {tariff ? (
        <FormSection title={tr('billing', 'rates_under_this_tariff')} icon={ListChecks} columns={1}>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="w-56" placeholder={tr('billing', 'search_services')} value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={keepEnterInCell} />
            <Button
              type="button"
              className="ms-auto"
              onClick={() => {
                setError(null);
                savePrices.mutate();
              }}
              loading={savePrices.isPending}
              disabled={pendingRates === 0}
            >
              {tr('billing', 'save')} {pendingRates || ''} {tr('billing', 'rate')}
              {pendingRates === 1 ? '' : 's'}
            </Button>
          </div>

          <DataTable
            rows={charges.data?.data}
            loading={charges.isLoading || prices.isLoading}
            rowKey={(row) => row.id}
            empty={tr('billing', 'no_services_match')}
            columns={[
              { header: tr('billing', 'code'), width: '120px', cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
              {
                header: tr('billing', 'service'),
                cell: (row) =>
                  row.category === 'package' ? (
                    <span className="flex flex-wrap items-center gap-2">
                      {row.name}
                      <Badge variant="info">{tr('billing', 'package')}</Badge>
                    </span>
                  ) : (
                    row.name
                  ),
              },
              { header: tr('billing', 'list_price'), align: 'right', cell: (row) => money(row.basePriceMinor) },
              {
                header: tr('billing', 'this_tariff'),
                align: 'right',
                cell: (row) => {
                  const current = priced.get(row.id);
                  return current ? <span className="tabular">{money(current.priceMinor)}</span> : <span className="text-muted-foreground">—</span>;
                },
              },
              {
                header: tr('billing', 'new_rate'),
                width: '130px',
                cell: (row) => (
                  <Input
                    type="text"
                    dir="ltr"
                    inputMode="decimal"
                    className="tabular"
                    placeholder={formatMoneyInput(priced.get(row.id)?.priceMinor ?? row.basePriceMinor)}
                    value={draft[row.id]?.price ?? ''}
                    onKeyDown={keepEnterInCell}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        [row.id]: { price: e.target.value, discount: draft[row.id]?.discount ?? '' },
                      })
                    }
                  />
                ),
              },
              {
                header: tr('billing', 'disc'),
                width: '90px',
                cell: (row) => (
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    className="tabular"
                    placeholder={String(priced.get(row.id)?.discountPct ?? 0)}
                    value={draft[row.id]?.discount ?? ''}
                    onKeyDown={keepEnterInCell}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        [row.id]: { price: draft[row.id]?.price ?? '', discount: e.target.value },
                      })
                    }
                  />
                ),
              },
              {
                header: '',
                align: 'right',
                width: '90px',
                cell: (row) =>
                  priced.has(row.id) ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      loading={removePrice.isPending && removePrice.variables === row.id}
                      onClick={() => {
                        setError(null);
                        removePrice.mutate(row.id);
                      }}
                    >
                      {tr('billing', 'remove')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        </FormSection>
      ) : (
        <p className="text-sm text-muted-foreground">{tr('billing', 'create_the_tariff_first_then_reopen_it_to_set')}</p>
      )}

      <FormActions
        submitLabel={tariff ? tr('billing', 'save_tariff') : tr('billing', 'create_tariff')}
        loading={saveTariff.isPending}
        disabled={automatic}
        incomplete={missing.length > 0}
        cancelTo={returnTo}
        note={!automatic && missing.length > 0 ? `${tr('billing', 'still_needed')} ${missing.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
